import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  MessageSquare, Phone, Mail, User, Clock, CheckCircle, XCircle,
  Loader2, Trash2, ChevronDown, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import authAxios, { API } from '../../utils/authAxios';

export default function AdminAsesoria() {
  var [requests, setRequests] = useState([]);
  var [stats, setStats] = useState({ pending: 0, contacted: 0, completed: 0 });
  var [loading, setLoading] = useState(true);
  var [filter, setFilter] = useState('all');
  var [selectedRequest, setSelectedRequest] = useState(null);
  var [updating, setUpdating] = useState(false);

  useEffect(function() {
    loadRequests();
  }, [filter]);

  async function loadRequests() {
    setLoading(true);
    try {
      var url = API + '/admin/asesoria-requests';
      if (filter !== 'all') url += '?status=' + filter;
      var res = await authAxios.get(url, { withCredentials: true });
      setRequests(res.data.requests || []);
      setStats(res.data.stats || { pending: 0, contacted: 0, completed: 0 });
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar solicitudes');
    }
    setLoading(false);
  }

  async function updateStatus(requestId, newStatus, notes) {
    setUpdating(true);
    try {
      await authAxios.put(API + '/admin/asesoria-requests/' + requestId, {
        status: newStatus,
        notes: notes
      }, { withCredentials: true });
      toast.success('Solicitud actualizada');
      loadRequests();
      setSelectedRequest(null);
    } catch (e) {
      toast.error('Error al actualizar');
    }
    setUpdating(false);
  }

  async function deleteRequest(requestId) {
    if (!window.confirm('¿Eliminar esta solicitud?')) return;
    try {
      await authAxios.delete(API + '/admin/asesoria-requests/' + requestId, { withCredentials: true });
      toast.success('Solicitud eliminada');
      loadRequests();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return '-';
    var d = new Date(dateStr);
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  function getStatusBadge(status) {
    var styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      contacted: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      completed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    var labels = {
      pending: 'Pendiente',
      contacted: 'Contactado',
      completed: 'Completado',
      cancelled: 'Cancelado'
    };
    return (
      <span className={'px-2 py-1 text-xs font-medium rounded-full border ' + (styles[status] || styles.pending)}>
        {labels[status] || status}
      </span>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
              <p className="text-slate-400 text-sm">Pendientes</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-400">{stats.contacted}</p>
              <p className="text-slate-400 text-sm">Contactados</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">{stats.completed}</p>
              <p className="text-slate-400 text-sm">Completados</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{requests.length}</p>
              <p className="text-slate-400 text-sm">Total</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'contacted', 'completed', 'cancelled'].map(function(status) {
          return (
            <Button
              key={status}
              variant={filter === status ? 'default' : 'outline'}
              size="sm"
              onClick={function() { setFilter(status); }}
              className={filter === status ? 'bg-emerald-600' : 'border-slate-700 text-slate-300'}
            >
              {status === 'all' ? 'Todos' : status === 'pending' ? 'Pendientes' : status === 'contacted' ? 'Contactados' : status === 'completed' ? 'Completados' : 'Cancelados'}
            </Button>
          );
        })}
      </div>

      {/* Requests List */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Solicitudes de Asesoría ({requests.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No hay solicitudes</p>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {requests.map(function(req) {
                return (
                  <div 
                    key={req._id || req.id} 
                    className={`p-4 bg-slate-800/50 rounded-lg border transition-colors cursor-pointer ${selectedRequest === req._id ? 'border-emerald-500' : 'border-slate-700 hover:border-slate-600'}`}
                    onClick={function() { setSelectedRequest(selectedRequest === req._id ? null : req._id); }}
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <h4 className="text-white font-medium">{req.name || 'Sin nombre'}</h4>
                          {getStatusBadge(req.status)}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-400">
                          {req.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {req.phone}
                            </span>
                          )}
                          {req.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {req.email}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {formatDate(req.created_at)}
                          </span>
                        </div>
                        {req.message && (
                          <p className="text-slate-400 text-sm mt-2 line-clamp-2">{req.message}</p>
                        )}
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={function(e) { e.stopPropagation(); deleteRequest(req._id); }}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Expanded Actions */}
                    {selectedRequest === req._id && (
                      <div className="mt-4 pt-4 border-t border-slate-700 space-y-3" onClick={function(e) { e.stopPropagation(); }}>
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            onClick={function() { updateStatus(req._id, 'contacted'); }}
                            disabled={updating}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Marcar Contactado
                          </Button>
                          <Button
                            size="sm"
                            onClick={function() { updateStatus(req._id, 'completed'); }}
                            disabled={updating}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            Marcar Completado
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={function() { updateStatus(req._id, 'cancelled'); }}
                            disabled={updating}
                            className="border-red-500/50 text-red-400"
                          >
                            Cancelar
                          </Button>
                        </div>
                        {req.notes && (
                          <p className="text-slate-400 text-sm">
                            <span className="font-medium">Notas:</span> {req.notes}
                          </p>
                        )}
                        {req.patient_type && (
                          <p className="text-slate-400 text-sm">
                            <span className="font-medium">Tipo:</span> {req.patient_type}
                          </p>
                        )}
                        {req.urgency && (
                          <p className="text-slate-400 text-sm">
                            <span className="font-medium">Urgencia:</span> {req.urgency}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
